import React, { useState } from "react";

import ColorPicker from "components/basic/color-picker/ColorPicker";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import { DiffOptions, ScadaEditorProps, ScadaLabel, TagHighlight } from "../scadaTypes";
import { numberConditions } from "../utils/const";

type ScadaTagEditorProps = ScadaEditorProps & {
  tag?: ScadaLabel;
  tableId?: string;
  rowId?: string;
  isInTextTagTable?: boolean;
  onClose: () => void;
};

const defaultDiffOptions: DiffOptions = {
  isIndicatorVisible: false,
  isPercentageVisible: false,
  decimalPlaces: undefined,
};

const defaultHighlight: TagHighlight = {
  value: 0,
  condition: "gte",
  bgColor: "#FF0000",
  textColor: "#000000",
  isBlinking: false,
};

const ScadaTagEditor: React.FC<ScadaTagEditorProps> = ({
  tag,
  onClose,
  tableId,
  rowId,
  isInTextTagTable,
  config,
  onConfigChange,
}) => {
  const [highlights, setHighlights] = useState<TagHighlight[]>(() =>
    tag?.highlights?.length ? tag.highlights : []
  );
  const [tagOptions, setTagOptions] = useState<Pick<ScadaLabel, "isRound" | "roundSize">>({
    isRound: tag?.isRound ?? false,
    roundSize: tag?.roundSize ?? 36,
  });

  const [diffOptions, setDiffOptions] = useState<ScadaLabel["diffOptions"]>({
    isIndicatorVisible: tag?.diffOptions?.isIndicatorVisible ?? false,
    isPercentageVisible: tag?.diffOptions?.isPercentageVisible ?? false,
    decimalPlaces: tag?.diffOptions?.decimalPlaces,
  });

  const handleHighlightsChange = (
    value: string | number | boolean | null,
    field: keyof TagHighlight,
    index: number
  ) => {
    setHighlights(prev => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };

  const handleSave = () => {
    if (isInTextTagTable) {
      onConfigChange({
        ...config,
        freeTextTagTables: config.freeTextTagTables.map(table =>
          table.id === tableId
            ? {
                ...table,
                rows: table.rows.map(row =>
                  row.id === rowId
                    ? { ...row, tag: { ...row.tag, highlights, ...tagOptions, diffOptions } }
                    : row
                ),
              }
            : table
        ),
      });
      onClose();
      return;
    }
    onConfigChange({
      ...config,
      elements: config.elements.map(el =>
        el.identifier === tag?.identifier ? { ...el, highlights, ...tagOptions, diffOptions } : el
      ),
    });
    onClose();
  };

  const renderNumInput = (value: number, index: number) => {
    return (
      <InputNumber
        value={value}
        onChange={e => handleHighlightsChange(e.value, "value", index)}
        maxFractionDigits={5}
        useGrouping={false}
        placeholder={T("word.value")}
      />
    );
  };

  const renderDropdown = (value: string, index: number) => {
    return (
      <Dropdown
        value={value}
        options={numberConditions}
        optionValue="value"
        onChange={e => handleHighlightsChange(e.value, "condition", index)}
      />
    );
  };

  const renderColorPicker = (color: string, field: keyof TagHighlight, index: number) => {
    return (
      <ColorPicker value={color} onValueChange={e => handleHighlightsChange(e.target.value, field, index)} />
    );
  };

  const renderSwitch = (checked: boolean, index: number) => {
    return (
      <InputSwitch checked={checked} onChange={e => handleHighlightsChange(e.value, "isBlinking", index)} />
    );
  };

  const renderActions = (_rowData: TagHighlight, options: ColumnBodyOptions) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-danger"
        onClick={() => setHighlights(prev => prev.toSpliced(options.rowIndex, 1))}
      />
    );
  };

  const updateDiffOptions = (patch: Partial<ScadaLabel["diffOptions"]>) => {
    setDiffOptions(prev => ({
      ...(prev ?? defaultDiffOptions),
      ...patch,
    }));
  };

  const columns: TableColumn<TagHighlight>[] = [
    {
      field: "value",
      header: T("word.value"),
      columnKey: "value",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderNumInput(rowData.value, options.rowIndex),
    },
    {
      field: "condition",
      header: T("word.condition"),
      columnKey: "condition",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderDropdown(rowData.condition, options.rowIndex),
    },
    {
      field: "bgColor",
      header: T("word.background-color"),
      columnKey: "bgColor",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderColorPicker(rowData.bgColor, "bgColor", options.rowIndex),
    },
    {
      field: "textColor",
      header: T("word.text-color"),
      columnKey: "textColor",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderColorPicker(rowData.textColor, "textColor", options.rowIndex),
    },
    {
      field: "isBlinking",
      header: T("word.is-blinking"),
      columnKey: "isBlinking",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderSwitch(Boolean(rowData.isBlinking), options.rowIndex),
    },
  ];
  return (
    <div className="flex flex-column gap-3">
      <div className="flex">
        <InputLabelWrapper labelName={T("word.round")} col={2}>
          <InputSwitch
            checked={tagOptions.isRound ?? false}
            onChange={e => setTagOptions(prev => ({ ...prev, isRound: e.value }))}
            className="mt-2"
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName={T("word.round-size")} col={2}>
          <InputNumber
            value={tagOptions.roundSize}
            onChange={e => setTagOptions(prev => ({ ...prev, roundSize: e.value! }))}
            maxFractionDigits={1}
            useGrouping={false}
            placeholder={T("word.round-size")}
            suffix=" px"
            disabled={!tagOptions.isRound}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName={T("word.show-diff-indicator")} col={2}>
          <InputSwitch
            checked={diffOptions?.isIndicatorVisible ?? false}
            onChange={e => updateDiffOptions({ isIndicatorVisible: e.value })}
            className="mt-2"
          />
        </InputLabelWrapper>
        {diffOptions?.isIndicatorVisible && (
          <>
            <InputLabelWrapper labelName={T("word.show-diff-in-percentage")} col={3}>
              <InputSwitch
                checked={diffOptions?.isPercentageVisible ?? false}
                onChange={e => updateDiffOptions({ isPercentageVisible: e.value })}
                className="mt-2"
              />
            </InputLabelWrapper>
            {diffOptions.isPercentageVisible && (
              <InputLabelWrapper labelName={T("config.decimal-places")} col={3}>
                <InputNumber
                  value={diffOptions?.decimalPlaces}
                  onChange={e => updateDiffOptions({ decimalPlaces: e.value ?? undefined })}
                  maxFractionDigits={1}
                  useGrouping={false}
                  placeholder={T("config.decimal-places")}
                />
              </InputLabelWrapper>
            )}
          </>
        )}
      </div>
      <DataTable value={highlights}>
        {columns.map(col => (
          <Column key={col.field} {...col} />
        ))}
        <Column columnKey="actions" body={renderActions} />
      </DataTable>
      <Button
        type="button"
        label={T("action.add-row")}
        icon="pi pi-plus"
        className="component-editor-button icon-green w-2"
        onClick={() => setHighlights([...highlights, defaultHighlight])}
      />
      <div className="flex justify-content-end gap-2">
        <Button
          type="button"
          label={T("action.close")}
          icon="pi pi-times"
          className="component-editor-button icon-red"
          onClick={onClose}
        />
        <Button
          type="button"
          label={T("action.save")}
          icon="pi pi-check"
          className="component-editor-button icon-green"
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default ScadaTagEditor;
